// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../../../services/notification-service';
import { TranslateService } from '@ngx-translate/core';
import { IProvisioningTemplate, DiskPasswordSetting, DiskProtectionMode, DriveTypes } from '../../../types/drive';
import { D3eService } from '../../../services/d3e-service';


@Component({
    selector: 'app-provisioning-template',
    templateUrl: './provisioning-template.component.html',
    styleUrls: ['./provisioning-template.component.scss'],
    standalone: false
})
export class ProvisioningTemplateComponent {
    provisioningForm: UntypedFormGroup;
    enableRansomwareDetection: boolean = true;
    // diskPassword = '';
    diskPasswordSettingType = DiskPasswordSetting;
    diskProtectionMode = DiskProtectionMode;

    constructor(public dialogRef: MatDialogRef<ProvisioningTemplateComponent>,
                private formBuilder: UntypedFormBuilder,
                private notification: NotificationService,
                private translate: TranslateService,
                private d3eService: D3eService,
                @Inject(MAT_DIALOG_DATA) public data: any) {

      let diskPasswordSetting = DiskPasswordSetting.AUTO_GENERATE;
      // let diskPassword = '';
      let diskProtectionMode = DiskProtectionMode.SINGLE_DURING_THREATS;
      let drive256GB = 10;
      let drive256GBPercent = true;
      let drive512GB = 10;
      let drive512GBPercent = true;
      let drive1TB = 50;
      let drive1TBPercent = false;
      let drive2TB = 50;
      let drive2TBPercent = false;
      let drive4TB = 50;
      let drive4TBPercent = false;

      if (data.provisioningTemplate?.id) {
        const drive256GBType: DriveTypes = this.data.provisioningTemplate.driveTypes.find(d => d.name === '256GB');
        const drive512GBType: DriveTypes = this.data.provisioningTemplate.driveTypes.find(d => d.name === '512GB');
        const drive1TBType: DriveTypes = this.data.provisioningTemplate.driveTypes.find(d => d.name === '1TB');
        const drive2TBType: DriveTypes = this.data.provisioningTemplate.driveTypes.find(d => d.name === '2TB');
        const drive4TBType: DriveTypes = this.data.provisioningTemplate.driveTypes.find(d => d.name === '4TB');

        drive256GB = drive256GBType?.size;
        drive256GBPercent = drive256GBType?.percent;
        drive512GB = drive512GBType?.size;
        drive512GBPercent = drive512GBType?.percent;
        drive1TB = drive1TBType?.size;
        drive1TBPercent = drive1TBType?.percent;
        drive2TB = drive2TBType?.size;
        drive2TBPercent = drive2TBType?.percent;
        drive4TB = drive4TBType?.size;
        drive4TBPercent = drive4TBType?.percent;

        diskPasswordSetting = data.provisioningTemplate.diskPasswordSetting;
        // diskPassword = data.provisioningTemplate.diskPasswordSetting === DiskPasswordSetting.AUTO_GENERATE ? '' : '*'.repeat(32);
        diskProtectionMode = data.provisioningTemplate.protectionMode;
        this.enableRansomwareDetection = data.provisioningTemplate.diskRansomware;
      }

      this.provisioningForm = this.formBuilder.group({
        drive256GB: [drive256GB, Validators.required],
        drive256GBPercent: [drive256GBPercent, Validators.required],
        drive512GB: [drive512GB, Validators.required],
        drive512GBPercent: [drive512GBPercent, Validators.required],
        drive1TB: [drive1TB, Validators.required],
        drive1TBPercent: [drive1TBPercent, Validators.required],
        drive2TB: [drive2TB, Validators.required],
        drive2TBPercent: [drive2TBPercent, Validators.required],
        drive4TB: [drive4TB, Validators.required],
        drive4TBPercent: [drive4TBPercent, Validators.required],
        diskPasswordSetting: [diskPasswordSetting, Validators.required],
        // diskPassword: [diskPassword, Validators.nullValidator],
        protectionMode: [diskProtectionMode, Validators.required],
        diskRansomware: [this.enableRansomwareDetection, Validators.required]
      });

  }

  get ctrls() {
      return this.provisioningForm.controls;
  }

  onDriveSizeUnitChange(event) {
    // console.log(event);
  }

  onRansomwareDetectionChange(checked: boolean) {
    this.enableRansomwareDetection = checked;
  }

  get formCtrls() {
    return this.provisioningForm.controls;
  }

  onDiskPasswordModeChanged(event) {
    // if (event.value === DiskPasswordSetting.AUTO_GENERATE) {
    //   this.provisioningForm.controls['diskPassword'].setValue('');
    // }
  }

  onProtectionModeChanged() {

  }

  onCancelTemplate() {
      this.dialogRef.close();
  }

  onSaveTemplate() {
    if (this.provisioningForm.invalid) {
      return;
    }

    const provisioningTemplate: IProvisioningTemplate = {
      driveTypes: [
        {name: '256GB', size: this.provisioningForm.value.drive256GB, percent: this.provisioningForm.value.drive256GBPercent},
        {name: '512GB', size: this.provisioningForm.value.drive512GB, percent: this.provisioningForm.value.drive512GBPercent},
        {name: '1TB', size: this.provisioningForm.value.drive1TB, percent: this.provisioningForm.value.drive1TBPercent},
        {name: '2TB', size: this.provisioningForm.value.drive2TB, percent: this.provisioningForm.value.drive2TBPercent},
        {name: '4TB', size: this.provisioningForm.value.drive4TB, percent: this.provisioningForm.value.drive4TBPercent},
      ],
      diskPasswordSetting: this.provisioningForm.value.diskPasswordSetting,
      // diskPassword: this.provisioningForm.value.diskPassword,
      protectionMode: this.provisioningForm.value.protectionMode,
      diskRansomware: this.provisioningForm.value.diskRansomware
    };

    if (this.data.provisioningTemplate?.id) {
      this.d3eService.updateProvisioningTemplate(this.data.selectedOrgId, provisioningTemplate)
        .subscribe(
          result => {
            this.dialogRef.close(result);
          },
          () => {
            this.notification.showError("Error updating secure drive provisioning template(s).");
          });
    } else {
      this.d3eService.createProvisioningTemplate(this.data.selectedOrgId, provisioningTemplate)
        .subscribe(
          result => {
            this.dialogRef.close(result);
          },
          () => {
            this.notification.showError("Error creating secure drive provisioning template(s).");
          });
    }
  }

}
